import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { messageHandler } from "utils/messageHandler";
import { AGENT_MESSAGES } from "utils/messages";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import {
  addressLine1Validation,
  cityValidation,
  emailValidation,
  firstNameValidation,
  lastNameValidation,
  passwordValidation,
  phoneValidation,
  stateValidation,
  statusValidation,
  zipcodeValidation,
} from "validationSchemas/common";
import AgentForm, { AgentFormValues, SelectOption } from "components/AgentsAddEditComponent";
import { uploadCloudFile, getCloudFileById, resolveImageId, hardDeleteCloudFile } from "components/Imageshelper";
import { getAuthCookie } from "../../utils/authCookies";

type BrokerageFirm = {
  id: string;
  firmDetails: { companyName: string };
};

// Select2 type for brokerage firms
type BrokerageOption = {
  value: string;
  label: string;
  id: string;
  firmDetails: {
    companyName: string;
    companyID?: string;
    companyWebsite?: string;
  };
};

type Office = {
  _id: string;
  officeName: string;
  officeType: string;
  officeAddress: Array<{
    _id: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  }>;
};

type AgentAPI = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone1: string;
  phone2?: string;
  status: string;
  profilePic?: string | null;
  created_by?: { id?: string; name?: string };
  firmDetails?: {
    address?: Partial<{
      addressLine1: string;
      addressLine2: string;
      city: string;
      state: string;
      country: string;
      zip: string;
      cityCode: string;
      stateCode: string;
      countryCode: string;
      latitude: number;
      longitude: number;
    }>;
  };
  agentDetails?: Partial<{
    brokerageFirmAffiliation: string; // a name on your API
    brokerageFirmID: string; // brokerage firm ID
    agentID: string;
    licenseNumber: string;
    licenseExpiryDate: string;
  }>;
  offices?: Array<{
    _id: string;
    officeName: string;
    officeType: string;
  }>;
};

const STATUS_OPTIONS: SelectOption[] = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];
const US_COUNTRY_DEFAULTS = { country: "United States", countryCode: "US" } as const;

const toUrlString = (res: unknown): string | undefined => {
  if (!res) return undefined;
  if (typeof res === "string") return res;
  const r = res as any;
  const u = r?.url ?? r?.signedUrl ?? r?.data?.url;
  return typeof u === "string" ? u : undefined;
};


export default function SignageAddEditAgent() {
  const { agentId } = useParams<{ agentId: string }>();
  const isEdit = Boolean(agentId);
  const navigate = useNavigate();
  const location = useLocation();
  const token = getAuthCookie("sessionToken") || "";
  const userId = getAuthCookie("userId") || "";

  // Determine the base route based on current location
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';

  // data sources (always arrays)
  const [offices, setOffices] = useState<Office[]>([]);
  const [agent, setAgent] = useState<AgentAPI | null>(null);
  const [loading, setLoading] = useState<boolean>(isEdit);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [serverImageSrc, setServerImageSrc] = useState<string | undefined>(undefined);
  const [cloudFileId, setCloudFileId] = useState<string | null>(null);
  
  // State for parent signage firm (for signageteammember login)
  const [parentSignageId, setParentSignageId] = useState<string | null>(null);
  const [parentSignageName, setParentSignageName] = useState<string | null>(null);
  
  // State for AsyncPaginate selected options
  const [selectedBrokerageOption, setSelectedBrokerageOption] = useState<BrokerageOption | null>(null);
  
  // Fetch parent hierarchy for signageteammember login
  const fetchParentHierarchy = async () => {
    if (!isSignageTeammember || !token) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/parent-hierarchy`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData?.error || errorData?.message || "Failed to fetch parent hierarchy";
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      // Find the parent with license_type "SIGNAGE_FIRM"
      const signageFirm = data.parent_chain?.find(
        (parent: any) => parent.license_type === "SIGNAGE_FIRM"
      );
      
      if (signageFirm) {
        setParentSignageId(signageFirm.id || signageFirm._id);
        setParentSignageName(signageFirm.name);
      }
    } catch (error) {
      console.error("Error fetching parent hierarchy:", error);
      messageHandler.error("agent", "fetchSingle", new Error(AGENT_MESSAGES.fetchParentHierarchy.error));
    }
  };

  // Helper function and constants for AsyncPaginate
  const normalizeId = (row: any) => String(row?.value ?? row?.id ?? row?._id ?? "").trim();
  const PAGE_SIZE = 10;
  
  // ===== Brokerage Firm Loader =====
  const loadBrokerageOptions: LoadOptions<BrokerageOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;
      const ownerId = (isSignageTeammember && parentSignageId ? parentSignageId : userId) || "";

      if (!ownerId) {
        return { options: [], hasMore: false, additional: { page } };
      }

      try {
        const q = (inputValue || "").trim();

        const params = new URLSearchParams({
          licenseType: "BROKERAGE_FIRM",
          query: q,
          page: String(page),
          limit: String(PAGE_SIZE),
        });

        const url = `${process.env.REACT_APP_API_URL}/customer-groups/${ownerId}/users-created-only?${params}`;

        const response = await fetch(url, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData?.error || errorData?.message || "Failed to fetch brokerage firms";
          console.error("Failed to fetch brokerage firms:", errorMessage);
          messageHandler.error("agent", "fetchSingle", new Error(AGENT_MESSAGES.fetchBrokerageFirms.error));
          return { options: [], hasMore: false, additional: { page } };
        }

        const json = await response.json();
        const list = Array.isArray(json?.data) ? json.data : [];

        const options: BrokerageOption[] = list.map((u: any) => {
          const companyName =
            u?.firmDetails?.companyName ||
            u?.company_name ||
            u?.label ||
            u?.text ||
            "(no name)";
          return {
            value: normalizeId(u),
            label: companyName,
            id: normalizeId(u),
            firmDetails: {
              companyName,
              companyID: u?.firmDetails?.companyID || u?.id || normalizeId(u),
              companyWebsite: u?.firmDetails?.companyWebsite || "",
            },
          };
        });

        const currentPage = Number(json?.page ?? page);
        const totalPages = Number(json?.totalPages ?? currentPage);
        const hasMore = currentPage < totalPages && list.length >= PAGE_SIZE;

        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (err) {
        console.error("Failed to load brokerage options:", err);
        messageHandler.error("agent", "fetchSingle", new Error(AGENT_MESSAGES.fetchBrokerageFirms.error));
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [userId, isSignageTeammember, parentSignageId, token]
  );

  // Fetch parent hierarchy for signageteammember login
  useEffect(() => {
    if (isSignageTeammember && token) {
      fetchParentHierarchy();
    }
  }, [isSignageTeammember, token]);
  
  const normalizePath = (p: string) => p.replace(/\\/g, "/").replace(/^\/+/, "");
  const buildAbsoluteFromRelative = (p: string) => {
    const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
    return `${base}/${normalizePath(p)}`;
  };
  useEffect(() => {
    let active = true;

    (async () => {
      const val = agent?.profilePic;
      if (!val) {
        if (active) setServerImageSrc(undefined);
        return;
      }

      if (/^https?:\/\//i.test(val)) {
        if (active) setServerImageSrc(val);
        return;
      }

      if (/^[0-9a-f]{24}$/i.test(val)) {
        setCloudFileId(val);
        try {
          const res = await getCloudFileById(val, token as string);
          // console.log(res,"tetetetete")
          const u = toUrlString(res);
          if (active) setServerImageSrc(u);
        } catch (err) {
          console.error("Failed to load profile image:", err);
          // Optionally show error for image loading - comment out if you want silent failures for images
          messageHandler.error("agent", "fetchSingle", new Error(AGENT_MESSAGES.fetchImage.error));
          if (active) setServerImageSrc(undefined);
        }
        return;
      }

      if (active) setServerImageSrc(buildAbsoluteFromRelative(val));
    })();

    return () => {
      active = false;
    };
  }, [agent?.profilePic, token]); // ⬅️ removed serverImageSrc from deps

  // Removed fetch firms useEffect - now using AsyncPaginate



  // ---------- fetch agent (edit) ----------
  useEffect(() => {
    if (!isEdit) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/users/${agentId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = (await res.json()) as AgentAPI;
        if (!res.ok) {
          const errorMessage = (data as any)?.error || (data as any)?.message || AGENT_MESSAGES.fetchSingle.error;
          throw new Error(errorMessage);
        }
        setAgent(data);
      } catch (e: any) {
        messageHandler.error("agent", "fetchSingle", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, agentId, token]);
  const DEFAULT_TEN_ZEROS = "0000000000";
  const withDefault = (v?: string, def = DEFAULT_TEN_ZEROS) =>
    !v || v.trim() === "" || v === def ? def : v;
  const toCoordinate = (value: unknown) => {
    const coordinate = Number(value ?? 0);
    return Number.isFinite(coordinate) ? coordinate : 0;
  };

  // ---------- initial values ----------
  const initialValues: AgentFormValues = useMemo(
    () => ({
      // personal
      firstName: agent?.firstName || "",
      lastName: agent?.lastName || "",
      phone1: agent?.phone1 || DEFAULT_TEN_ZEROS,
      phone2: agent?.phone2 || "",
      // account
      email: agent?.email || "",
      password: "",
      // professional
      brokerageFirmAffiliation: "", // FIRM ID will be stored here
      officeId: "",
      agentID: agent?.agentDetails?.agentID || "",
      licenseNumber: agent?.agentDetails?.licenseNumber || "",
      licenseExpiryDate: agent?.agentDetails?.licenseExpiryDate || "",
      statuss: agent?.status || "Active",
      // address
      addressLine1: agent?.firmDetails?.address?.addressLine1 || "",
      addressLine2: agent?.firmDetails?.address?.addressLine2 || "",
      city: agent?.firmDetails?.address?.city || "",
      state: agent?.firmDetails?.address?.state || "",
      country: agent?.firmDetails?.address?.country || US_COUNTRY_DEFAULTS.country,
      zipcode: agent?.firmDetails?.address?.zip || "",
      cityCode: agent?.firmDetails?.address?.cityCode || "",
      stateCode: agent?.firmDetails?.address?.stateCode || "",
      countryCode: agent?.firmDetails?.address?.countryCode || US_COUNTRY_DEFAULTS.countryCode,
      latitude: toCoordinate(agent?.firmDetails?.address?.latitude),
      longitude: toCoordinate(agent?.firmDetails?.address?.longitude),
      // profile
      image: null,
      profilePic: agent?.profilePic || null,
      displaySrc: agent?.profilePic || null,
    }),
    [agent]
  );

  // ---------- formik (parent only) ----------
  const formik = useFormik<AgentFormValues>({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      firstName: firstNameValidation,
      lastName: lastNameValidation,
      phone1: phoneValidation,
      email: emailValidation,
      // password: isEdit ? Yup.string() : passwordValidation,
      // addressLine1: addressLine1Validation,
      // city: cityValidation,
      // state: stateValidation,
      // zipcode: zipcodeValidation,
      // statuss: statusValidation,
    }),
    onSubmit: async (values) => {
      console.log(values,"valuess");
      try {
        setFormSubmitting(true);

        let imageFileId = cloudFileId || ""; // keep existing if present
        
        // ✅ Delete old image if a new one is being uploaded
        if (values.image instanceof File && cloudFileId && /^[0-9a-f]{24}$/i.test(cloudFileId)) {
          try {
            await hardDeleteCloudFile(cloudFileId, token);
            console.log(`Old profile image ${cloudFileId} deleted successfully`);
          } catch (deleteError) {
            console.warn(`Failed to delete old profile image ${cloudFileId}:`, deleteError);
            // Continue even if delete fails - don't block the update
          }
        }

        // Upload new image
        if (values.image instanceof File) {
          const uploaded = await uploadCloudFile({ file: values.image, token });
          imageFileId = uploaded.id;
        }
        
        const generated_id = await resolveImageId(values.image, token, imageFileId);
        // resolve firm from selected brokerage option
        const firm = selectedBrokerageOption;
        // resolve office by ID
        const office = offices.find((o) => o._id === values.officeId);
        const addressCountry = values.country?.trim() || US_COUNTRY_DEFAULTS.country;
        const addressCountryCode = values.countryCode?.trim() || US_COUNTRY_DEFAULTS.countryCode;
        const addressLatitude = toCoordinate(values.latitude);
        const addressLongitude = toCoordinate(values.longitude);

        const payload = {
          email: values.email,
          // password: values.password,
          password:"",
          // password: agentId? values.password : "",
          userType: "agent",
          firstName: values.firstName,
          // phone2: withDefault(values.phone2),
          phone1: withDefault(values.phone1),
          lastName: values.lastName,
          // phone1: values.phone1,
          phone2: values.phone2 || "",
          licenseType: selectedBrokerageOption ? "BROKERAGE_AGENT" : "AGENT",
          status: values.statuss,
          profilePic: generated_id || undefined,
          firmDetails: {
            companyID: firm?.firmDetails?.companyID || "",
            companyName: firm?.firmDetails?.companyName || "",
            companyWebsite: firm?.firmDetails?.companyWebsite || "",
            address: {
              addressLine1: values.addressLine1,
              addressLine2: values.addressLine2,
              city: values.city,
              state: values.state,
              country: addressCountry,
              zip: values.zipcode,
              cityCode: values.cityCode,
              stateCode: values.stateCode,
              countryCode: addressCountryCode,
              latitude: addressLatitude,
              longitude: addressLongitude,
              phone: withDefault(values.phone1),
            },
          },
          agentDetails: {
            brokerageFirmAffiliation: firm?.firmDetails?.companyName,
            brokerageFirmName: firm?.firmDetails?.companyName,
            brokerageFirmID: firm?.value,
            agentID: values.agentID,
            licenseNumber: values.licenseNumber,
            licenseExpiryDate: values.licenseExpiryDate,
            licenseActive: false,
          },
          offices: office
            ? [
              {
                _id: office._id,
                officeName: office.officeName,
                officeType: office.officeType,
                officeAddress:
                  office.officeAddress?.[0] && {
                    _id: office.officeAddress[0]._id,
                    addressLine1: office.officeAddress[0].addressLine1,
                    city: office.officeAddress[0].city,
                    state: office.officeAddress[0].state,
                    country: office.officeAddress[0].country,
                    zip: office.officeAddress[0].zip,
                  },
              },
            ]
            : [],
          created_by: firm ? { id: firm.value, name: firm.firmDetails.companyName } : undefined,
        };

        const url = isEdit
          ? `${process.env.REACT_APP_API_URL}/users/${agentId}`
          : `${process.env.REACT_APP_API_URL}/user/create`;
        const method = isEdit ? "PATCH" : "POST";

        const res = await fetch(url, {
          method,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
        const result = await res.json();
        if (!res.ok || result?.error) throw new Error(result?.error || "Failed to save agent");

        const hasAddressDetails = [
          values.addressLine1,
          values.city,
          values.state,
          values.zipcode,
        ].some((field) => field?.trim());

        // EXTRA: create address only on ADD
        if (!isEdit && hasAddressDetails) {
          try {
            const addrRes = await fetch(
              `${process.env.REACT_APP_API_URL}/addresses/user/${result.id}`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                  user_id: String(result.id),
                  label: `${values.firstName} ${values.lastName || ""}`,
                  first_name: values.firstName,
                  last_name: values.lastName,
                  company: "",
                  address_details: {
                    label: `${values.firstName} ${values.lastName || ""}`,
                    addressLine1: values.addressLine1,
                    addressLine2: values.addressLine2,
                    city: values.city,
                    state: values.state,
                    zip: values.zipcode,
                    country: addressCountry,
                    // phone2: withDefault(values.phone2),
                    phone: withDefault(values.phone1),
                    // phone: values.phone1,
                    cityCode: values.cityCode,
                    stateCode: values.stateCode,
                    countryCode: addressCountryCode,
                    latitude: addressLatitude,
                    longitude: addressLongitude,
                  },
                }),
              }
            );
            const addrJson = await addrRes.json();
            if (!addrRes.ok || addrJson?.error) {
              messageHandler.error("agent", "create", new Error( "Failed to create addresses"));
            }
          } catch { }
        }


        //   if (isEdit) {
        //   try {
        //     const addrRes = await fetch(
        //       `${process.env.REACT_APP_API_URL}/addresses/${addressId}`,
        //       {
        //         method: "PUT",
        //         headers: {
        //           "Content-Type": "application/json",
        //           Authorization: `Bearer ${token}`,
        //         },
        //         body: JSON.stringify({
        //           user_id: String(result.id),
        //           label: `${values.firstName} ${values.lastName || ""}`,
        //           first_name: values.firstName,
        //           last_name: values.lastName,
        //           company: "",
        //           address_details: {
        //             label: `${values.firstName} ${values.lastName || ""}`,
        //             addressLine1: values.addressLine1,
        //             addressLine2: values.addressLine2,
        //             city: values.city,
        //             state: values.state,
        //             zip: values.zipcode,
        //             country: values.country,
        //             // phone2: withDefault(values.phone2),
        //             phone: withDefault(values.phone1),
        //             // phone: values.phone1,
        //             cityCode: values.cityCode,
        //             stateCode: values.stateCode,
        //             countryCode: values.countryCode,
        //             latitude: values.latitude,
        //             longitude: values.longitude,
        //           },
        //         }),
        //       }
        //     );
        //     const addrJson = await addrRes.json();
        //     if (!addrRes.ok || addrJson?.error) {
        //       showError(addrJson?.error || "Failed to create address");
        //     }
        //   } catch { }
        // }

        messageHandler.success("agent", isEdit ? "update" : "create");
        navigate(`${baseRoute}/agents`);
      } catch (e: any) {
        messageHandler.error("agent", isEdit ? "update" : "create", e);
      } finally {
        setFormSubmitting(false);
      }
    },
  });

  // fetch offices when firm changes
  useEffect(() => {
    const firmId = formik.values.brokerageFirmAffiliation;
    if (!firmId) {
      setOffices([]);
      return;
    }

    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/offices-search?created_by.id=${firmId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        if (!res.ok || data?.error) throw new Error(data?.error || "Failed to fetch offices");
        setOffices(Array.isArray(data) ? (data as Office[]) : []);
      } catch (e: any) {
        messageHandler.error("agent", "fetch", e);
        setOffices([]);
      }
    })();
  }, [formik.values.brokerageFirmAffiliation, token]);

  // ---------- HYDRATE firm/office on EDIT ----------
  // 1) map agent firm name or created_by.id to firm ID → set in Formik and selectedBrokerageOption
  useEffect(() => {
    if (!isEdit || !agent) return;

    // Only set brokerage firm if agent has a valid brokerage firm affiliation
    // Check if agent has brokerage firm details - ignore created_by as it's set by signage firm
    const hasBrokerageAffiliation = agent.agentDetails?.brokerageFirmAffiliation && 
                                   agent.agentDetails?.brokerageFirmID &&
                                   agent.agentDetails?.brokerageFirmAffiliation.trim() !== "";
    
    if (hasBrokerageAffiliation) {
      // Use brokerage firm ID from agentDetails
      const firmId = agent.agentDetails?.brokerageFirmID || "";
      const firmName = agent.agentDetails?.brokerageFirmAffiliation || "";

      // Create a BrokerageOption object for the selected firm
      const brokerageOption: BrokerageOption = {
        value: firmId,
        label: firmName,
        id: firmId,
        firmDetails: {
          companyName: firmName,
          companyID: agent.agentDetails?.brokerageFirmID || firmId,
          companyWebsite: "",
        }
      };
      setSelectedBrokerageOption(brokerageOption);

      if (firmId && formik.values.brokerageFirmAffiliation !== firmId) {
        formik.setFieldValue("brokerageFirmAffiliation", firmId, false);
      }
    } else {
      // Clear brokerage selection if agent doesn't have brokerage affiliation
      setSelectedBrokerageOption(null);
      formik.setFieldValue("brokerageFirmAffiliation", "", false);
    }
  }, [isEdit, agent]); // removed firms dependency

  // 2) after offices arrive, set agent's officeId
  useEffect(() => {
    if (!isEdit || !agent || !offices.length) return;
    const agentOfficeId = agent.offices?.[0]?._id || "";
    if (agentOfficeId && formik.values.officeId !== agentOfficeId) {
      formik.setFieldValue("officeId", agentOfficeId, false);
    }
  }, [isEdit, agent, offices]); // keep deps

  if (loading) return <div className="content">Loading...</div>;

  // options for child (always arrays) - now using AsyncPaginate
  const brokerageFirmOptions: SelectOption[] = []; // Always empty since we're using AsyncPaginate

  const officeOptions: SelectOption[] = (offices ?? []).map((o) => ({
    value: o._id,
    label: o.officeName,
  }));
  const joinUrl = (base?: string, path?: string) => {
    if (!path) return "";
    if (!base) return path;
    if (/^https?:\/\//i.test(path)) return path;
    const b = base.replace(/\/+$/, "");
    const p = path.replace(/^\/+/, "");
    return `${b}/${p}`;
  };

  const displaySrc: string | undefined =
    serverImageSrc ??
    (typeof formik.values.profilePic === "string" && /^\/?uploads\//i.test(formik.values.profilePic)
      ? joinUrl(process.env.REACT_APP_BASE_URL, formik.values.profilePic)
      : undefined);

  return (
    <AgentForm
      isEdit={isEdit}
      formik={formik}
      brokerageFirmOptions={brokerageFirmOptions}
      officeOptions={officeOptions}
      statusOptions={STATUS_OPTIONS}
      displaySrc={displaySrc}
      onCancel={() => navigate(-1)}
      onSubmit={() => formik.handleSubmit()}
      submitting={formSubmitting}
      enableGoogleAddressDetailsFlow
      // AsyncPaginate props
      loadBrokerageOptions={loadBrokerageOptions}
      selectedBrokerageOption={selectedBrokerageOption}
      onBrokerageChange={setSelectedBrokerageOption}
    />
  );
}
